// Leitura de uma CONVERSA.
//
// A lista vem do índice; o CORPO vem do IMAP, e só ao abrir. É a decisão de
// desempenho que inviabilizou o Roundcube quando feita ao contrário.
//
// A tela mostra a conversa inteira, não uma mensagem solta. Veio de usar: ao
// abrir uma mensagem recebida não dava para ver a própria resposta sem ir até
// "Enviados" procurar — metade do diálogo ficava escondida.
//
// O corpo de cada mensagem continua vindo uma por vez, ao expandir. Baixar a
// conversa inteira do IMAP seria trinta idas para mostrar uma.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as correio from "../api/correio";
import { explicar } from "../api/problema";
import type { CursorDe, Mensagem, MensagemResumo } from "../api/tipos";

/** O formato que `useInfiniteQuery` guarda para a lista de mensagens. */
type InfinitoDeMensagens = { pages: CursorDe<MensagemResumo>[]; pageParams: unknown[] };
import { Icone } from "../ui/Icone";
import { corDoAvatar, dataCurta, dataLonga, inicial, nomeCurto, tamanho } from "../ui/formato";
import { Etiqueta, MenuDeMarcadores, apenasDoUsuario } from "./Marcadores";
import { MenuDePastas } from "./Pastas";

export type ModoDeResposta = "responder" | "responder_todos" | "encaminhar";

export function Leitura({
  aoResponder,
}: {
  aoResponder: (modo: ModoDeResposta, mensagem: Mensagem) => void;
}) {
  const { pasta = "INBOX", uuid = "" } = useParams();
  const navegar = useNavigate();
  const fila = useQueryClient();
  const [imagens, setImagens] = useState(false);

  const consulta = useQuery({
    queryKey: ["mensagem", uuid, imagens],
    queryFn: () => correio.ler(uuid, { carregar_imagens: imagens }),
  });

  // A conversa é resumo, e barata. Falhar aqui NÃO pode esconder a mensagem
  // aberta: se a conversa não vier, a tela cai no comportamento antigo e
  // mostra só ela. Ler o e-mail é o essencial; ver o histórico é o conforto.
  const fio = useQuery({
    queryKey: ["conversa", uuid],
    queryFn: ({ signal }) => correio.conversa(uuid, signal),
  });

  // Os marcadores já em uso na pasta viram sugestão: sem elas, cada mensagem
  // ganharia o mesmo marcador escrito de um jeito diferente.
  //
  // CHAVE PRÓPRIA, e isto é correção de defeito: esta consulta usava
  // ["mensagens", pasta], a MESMA da lista — que é `useInfiniteQuery` e
  // guarda {pages:[…]}. Esta guarda {itens:[…]}. Duas formas no mesmo lugar
  // do cache: quem montasse por último sobrescrevia a outra, e a lista
  // voltava sem `pages`. Chave diferente para dado diferente.
  const daPasta = useQuery({
    queryKey: ["marcadores-da-pasta", pasta],
    queryFn: () => correio.listar({ pasta, limite: 100 }),
  });
  const sugestoes = [
    ...new Set((daPasta.data?.itens ?? []).flatMap((m) => apenasDoUsuario(m.marcadores))),
  ].sort((a, b) => a.localeCompare(b, "pt-BR"));

  // ABRIR A MENSAGEM JÁ A MARCA COMO LIDA — no servidor. A lista que está no
  // cache não fica sabendo, e ao voltar da leitura a mensagem continuava em
  // negrito até alguém clicar em atualizar.
  //
  // O conserto NÃO é buscar mais vezes. O `refetchInterval` da lista só roda
  // enquanto ela está montada, e aqui ela não está; ao voltar, o react-query
  // vê o dado ainda fresco (`staleTime`) e não busca. Buscar de novo também
  // seria pedir ao servidor algo que já sabemos.
  //
  // Então o cache é CORRIGIDO no lugar, que é instantâneo e não gera
  // requisição. Os contadores das pastas, esses sim, vêm do servidor: o
  // número de não lidas é contado lá.
  useEffect(() => {
    if (!consulta.data?.uuid) return;
    fila.setQueriesData<InfinitoDeMensagens>(
      { queryKey: ["mensagens"] },
      (antigo) =>
        antigo?.pages
          ? {
              ...antigo,
              pages: antigo.pages.map((p) => ({
                ...p,
                itens: (p.itens ?? []).map((m) =>
                  m.uuid === uuid ? { ...m, lida: true } : m,
                ),
              })),
            }
          : antigo,
    );
    void fila.invalidateQueries({ queryKey: ["pastas"] });
  }, [consulta.data?.uuid, uuid, fila]);

  const invalidar = () => {
    void fila.invalidateQueries({ queryKey: ["mensagens"] });
    void fila.invalidateQueries({ queryKey: ["pastas"] });
  };

  const mover = useMutation({
    mutationFn: (destino: string) => correio.mover([uuid], destino),
    onSuccess: () => {
      invalidar();
      navegar(`/correio/${encodeURIComponent(pasta)}`);
    },
  });

  const apagar = useMutation({
    mutationFn: () => correio.apagar(uuid),
    onSuccess: () => {
      invalidar();
      navegar(`/correio/${encodeURIComponent(pasta)}`);
    },
  });

  if (consulta.isPending) {
    return <Painel>carregando…</Painel>;
  }
  if (consulta.isError) {
    return <Painel>{explicar(consulta.error)}</Painel>;
  }

  const m = consulta.data;
  const remetente = m.remetente ?? "";
  const anexos = (m.anexos ?? []).filter((a) => !a.embutido);

  // A conversa parte em duas, em torno da mensagem aberta: o que veio antes
  // fica acima, o que veio depois fica abaixo. É a ordem do diálogo, e é o
  // que põe a RESPOSTA QUE EU DEI logo abaixo da mensagem que a motivou —
  // o pedido que originou esta tela.
  //
  // A aberta é excluída das duas listas: ela já é desenhada no meio, inteira.
  const naConversa = fio.data?.itens ?? [];
  const posicao = naConversa.findIndex((i) => i.uuid === uuid);
  const antes = posicao > 0 ? naConversa.slice(0, posicao) : [];
  const depois = posicao >= 0 ? naConversa.slice(posicao + 1) : [];

  return (
    <div className="flex-1 flex flex-col bg-white rounded-tl-2xl overflow-hidden min-w-0">
      <div className="flex items-center gap-1 px-3 py-2 border-b border-linha-suave text-tinta-media">
        <Link
          to={`/correio/${encodeURIComponent(pasta)}`}
          className="p-2 rounded-full hover:bg-fundo"
          aria-label="Voltar"
          title="Voltar (u)"
        >
          <Icone nome="RotateCcw" tamanho={18} />
        </Link>
        <Botao icone="Archive" titulo="Arquivar (e)" rotulo="Arquivar" aoClicar={() => mover.mutate("Archive")} />
        {/* "Marcar como spam" só faz sentido no que você RECEBEU. Em Enviados
            e Rascunhos a mensagem é sua, e denunciar a si mesmo ao antispam
            ensina o filtro errado: o aprendizado é por caixa, e marcaria o
            próprio remetente como spam. */}
        {pasta !== "Sent" && pasta !== "Drafts" && (
          <Botao icone="ShieldAlert" titulo="Marcar como spam" rotulo="Spam" aoClicar={() => mover.mutate("Junk")} />
        )}
        <Botao icone="Trash2" titulo="Excluir (#)" rotulo="Excluir" aoClicar={() => apagar.mutate()} />
        <MenuDePastas atual={pasta} aoEscolher={(n) => mover.mutate(n)} />
        <MenuDeMarcadores uuid={uuid} atuais={m.marcadores ?? []} sugestoes={sugestoes} />
        <Botao
          icone="MailOpen"
          titulo="Marcar como não lida"
          aoClicar={() => {
            void correio.marcar(uuid, { lida: false }).then(() => {
              invalidar();
              navegar(`/correio/${encodeURIComponent(pasta)}`);
            });
          }}
        />
      </div>

      <div className="px-3 sm:px-6 py-4 overflow-y-auto">
        <h1 className="text-lg sm:text-xl text-tinta mb-2 break-words">
          {m.assunto || "(sem assunto)"}
          {naConversa.length > 1 && (
            <span className="ml-2 align-middle text-xs text-tinta-fraca whitespace-nowrap">
              {naConversa.length} mensagens
            </span>
          )}
        </h1>
        {apenasDoUsuario(m.marcadores).length > 0 && (
          <p className="mb-3">
            {apenasDoUsuario(m.marcadores).map((r) => (
              <Etiqueta key={r} nome={r} />
            ))}
          </p>
        )}

        {/* As anteriores ficam RECOLHIDAS, acima. Abertas de uma vez, uma
            conversa longa empurraria a mensagem que a pessoa veio ler para
            fora da tela — que é o oposto do que o recurso serve. */}
        {antes.map((a) => (
          <Recolhida key={a.uuid} resumo={a} pasta={pasta} />
        ))}

        <article className="rounded-lg border border-linha">
          <header className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 sm:px-4 py-3">
            <span
              className="h-9 w-9 rounded-full grid place-items-center text-white text-sm flex-shrink-0"
              style={{ background: corDoAvatar(remetente) }}
              aria-hidden="true"
            >
              {inicial(m.remetente_nome, remetente)}
            </span>
            <div className="flex-1 min-w-0 text-sm">
              <span className="font-semibold">{nomeCurto(m.remetente_nome, remetente)}</span>{" "}
              <span className="text-tinta-fraca">&lt;{remetente}&gt;</span>
              <div className="text-xs text-tinta-fraca truncate">
                para {(m.destinatarios ?? []).join(", ")}
              </div>
            </div>
            <span className="text-xs text-tinta-fraca whitespace-nowrap">{dataLonga(m.data)}</span>
          </header>

          <Autenticacao dados={m.autenticacao} />

          {/* O HTML já vem HIGIENIZADO do servidor (bluemonday). Não se
              higieniza de novo aqui: duas higienizações divergem com o tempo,
              e a do navegador seria a que ninguém testa.

              `[&_img[data-src-bloqueado]]:hidden` ESCONDE a imagem retida.
              O servidor já tirou o `src` dela, então o navegador não pede
              nada — mas um `<img>` sem src e com `alt` ainda desenha o texto
              alternativo solto no meio do e-mail, e "icone-calendario" no
              corpo da mensagem não ajuda ninguém. Quem explica o que houve é
              o aviso logo abaixo, que diz quantas foram e oferece carregar. */}
          {m.corpo_html ? (
            <div
              className="px-4 pb-4 text-sm text-tinta-media leading-relaxed [&_a]:text-acao [&_a]:underline [&_img]:max-w-full [&_img[data-src-bloqueado]]:hidden"
              dangerouslySetInnerHTML={{ __html: m.corpo_html }}
            />
          ) : (
            <pre className="px-4 pb-4 text-sm text-tinta-media leading-relaxed whitespace-pre-wrap font-sans">
              {m.corpo_texto}
            </pre>
          )}

          {!!m.imagens_bloqueadas && !imagens && (
            <div className="mx-4 mb-4 flex items-center gap-3 rounded-lg border border-aviso/40 bg-aviso/10 px-3 py-2 text-xs text-tinta">
              <Icone nome="Info" tamanho={16} className="text-aviso flex-shrink-0" />
              <span className="flex-1">
                {m.imagens_bloqueadas} imagem(ns) bloqueada(s). Carregá-las avisa quem enviou
                que você leu a mensagem, de onde e quando.
              </span>
              <button onClick={() => setImagens(true)} className="text-acao underline whitespace-nowrap">
                Carregar mesmo assim
              </button>
            </div>
          )}

          {anexos.length > 0 && (
            <div className="mx-4 mb-4 flex flex-wrap gap-3">
              {anexos.map((a) => (
                <Anexo key={a.parte} uuid={uuid} anexo={a} />
              ))}
            </div>
          )}
        </article>

        {/* O que veio DEPOIS — em geral a própria resposta. É por isto que a
            tela existe: antes era preciso ir até "Enviados" para saber o que
            se tinha respondido. */}
        {depois.map((d) => (
          <Recolhida key={d.uuid} resumo={d} pasta={pasta} />
        ))}

        <div className="flex flex-wrap gap-2 mt-3">
          <Acao icone="Reply" rotulo="Responder" aoClicar={() => aoResponder("responder", m)} />
          {/* "Responder a todos" só aparece quando há a quem: numa mensagem
              com um destinatário só, o botão faria exatamente o mesmo que o
              anterior e só teria a função de confundir. */}
          {(m.destinatarios?.length ?? 0) > 1 && (
            <Acao
              icone="ReplyAll"
              rotulo="Responder a todos"
              aoClicar={() => aoResponder("responder_todos", m)}
            />
          )}
          <Acao icone="Forward" rotulo="Encaminhar" aoClicar={() => aoResponder("encaminhar", m)} />
        </div>
      </div>
    </div>
  );
}

/**
 * Uma mensagem da conversa que NÃO é a aberta.
 *
 * Nasce recolhida, numa linha só, como no Gmail. O corpo só é buscado quando
 * alguém clica: a conversa vem do índice, e trazer trinta corpos do IMAP para
 * mostrar um seria pagar trinta vezes pelo que não se vai ler.
 */
function Recolhida({ resumo, pasta }: { resumo: MensagemResumo; pasta: string }) {
  const [aberta, setAberta] = useState(false);
  const remetente = resumo.remetente ?? "";

  const corpo = useQuery({
    // CHAVE PRÓPRIA — "espiada" não é "lida".
    //
    // Com a chave da leitura normal, abrir esta mensagem depois encontraria
    // o cache pronto, não chamaria o servidor, e portanto NÃO a marcaria como
    // lida lá. A tela diria "lida" e o servidor diria o contrário, até o
    // próximo recarregamento desfazer — o pior tipo de defeito deste produto,
    // porque parece ter funcionado.
    queryKey: ["mensagem-espiada", resumo.uuid],
    // `marcar_lida: false`: expandir para conferir o que já se respondeu não
    // é o mesmo gesto que ler algo novo, e marcar lida aqui faria mensagens
    // sumirem do contador sem ninguém ter aberto de fato.
    queryFn: () => correio.ler(resumo.uuid!, { marcar_lida: false }),
    enabled: aberta,
  });

  return (
    <article className="mb-2 rounded-lg border border-linha bg-fundo/40">
      <button
        onClick={() => setAberta((a) => !a)}
        aria-expanded={aberta}
        className="flex w-full items-center gap-3 px-3 sm:px-4 py-2.5 text-left hover:bg-fundo rounded-lg"
      >
        <span
          className="h-7 w-7 flex-shrink-0 rounded-full grid place-items-center text-white text-xs"
          style={{ background: corDoAvatar(remetente) }}
          aria-hidden="true"
        >
          {inicial(resumo.remetente_nome, remetente)}
        </span>
        {/* Duas linhas no celular, uma no desktop — mesma decisão da lista.
            Testado na tela: sem o trecho, a linha recolhida ficava só com o
            nome e a hora, e não dava para saber se valia abrir. */}
        <span className="min-w-0 flex-1 text-sm">
          <span className="block truncate font-medium sm:inline">
            {nomeCurto(resumo.remetente_nome, remetente)}
          </span>
          {!aberta && (
            <span className="block truncate text-xs text-tinta-fraca sm:ml-2 sm:inline sm:text-sm">
              {resumo.preview}
            </span>
          )}
        </span>
        {resumo.pasta && resumo.pasta !== pasta && (
          <span className="hidden sm:inline rounded bg-linha px-1.5 py-0.5 text-[11px] text-tinta-media whitespace-nowrap">
            {resumo.pasta}
          </span>
        )}
        <span className="text-xs text-tinta-fraca whitespace-nowrap">{dataCurta(resumo.data)}</span>
      </button>

      {aberta && (
        <div className="border-t border-linha px-3 sm:px-4 py-3">
          <p className="mb-2 text-xs text-tinta-fraca break-words">
            para {(resumo.destinatarios ?? []).join(", ")} · {dataLonga(resumo.data)}
          </p>
          {corpo.isPending && <p className="text-sm text-tinta-fraca">carregando…</p>}
          {corpo.isError && <p className="text-sm text-perigo">{explicar(corpo.error)}</p>}
          {corpo.data &&
            (corpo.data.corpo_html ? (
              <div
                className="text-sm text-tinta-media leading-relaxed [&_a]:text-acao [&_a]:underline [&_img]:max-w-full [&_img[data-src-bloqueado]]:hidden"
                dangerouslySetInnerHTML={{ __html: corpo.data.corpo_html }}
              />
            ) : (
              <pre className="text-sm text-tinta-media leading-relaxed whitespace-pre-wrap break-words font-sans">
                {corpo.data.corpo_texto}
              </pre>
            ))}
        </div>
      )}
    </article>
  );
}

function Autenticacao({ dados }: { dados?: { spf?: string; dkim?: string; dmarc?: string } }) {
  if (!dados) return null;
  const falhou = [dados.spf, dados.dkim, dados.dmarc].some((v) => v === "fail");
  if (!falhou) return null;
  // Só aparece quando FALHA. Um selo verde em toda mensagem legítima ensina o
  // usuário a ignorar o selo — e aí o aviso que importa passa despercebido.
  return (
    <div className="mx-4 mb-3 flex items-center gap-2 rounded-lg border border-perigo/40 bg-perigo/10 px-3 py-2 text-xs text-tinta">
      <Icone nome="AlertTriangle" tamanho={16} className="text-perigo flex-shrink-0" />
      <span>
        O remetente não pôde ser confirmado (SPF {dados.spf}, DKIM {dados.dkim}, DMARC{" "}
        {dados.dmarc}). Desconfie de pedidos de dinheiro ou de senha.
      </span>
    </div>
  );
}

/**
 * Nem toda parte tem nome de arquivo.
 *
 * As de uma DEVOLUÇÃO não têm: o relatório de entrega e a mensagem original
 * são partes MIME sem `filename`. O cartão ficava com um vazio onde deveria
 * estar o nome, parecendo anexo corrompido.
 */
function nomeDoAnexo(nome: string | undefined, tipo: string | undefined): string {
  if (nome?.trim()) return nome.trim();
  switch (tipo) {
    case "message/delivery-status":
      return "Relatório de entrega";
    case "message/rfc822":
      return "Mensagem original";
    case "text/plain":
      return "Texto sem nome";
    default:
      return tipo ? `Arquivo (${tipo})` : "Arquivo sem nome";
  }
}

function Anexo({
  uuid,
  anexo,
}: {
  uuid: string;
  anexo: { parte?: string; nome?: string; tipo_declarado?: string; tamanho_bytes?: number };
}) {
  const [baixando, setBaixando] = useState(false);
  const rotulo = nomeDoAnexo(anexo.nome, anexo.tipo_declarado);

  async function baixar() {
    setBaixando(true);
    try {
      const blob = await correio.baixarAnexo(uuid, anexo.parte!);
      // O servidor manda tudo como octet-stream de propósito: servir com o
      // tipo declarado deixaria HTML hostil executar no domínio do webmail.
      // Quem dá nome ao arquivo é esta linha, não o Content-Type.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // O nome do ARQUIVO salvo não pode ser o rótulo amigável: "Relatório de
      // entrega" viraria um arquivo sem extensão que o sistema não abre.
      a.download = anexo.nome || `${anexo.parte || "anexo"}.eml`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setBaixando(false);
    }
  }

  return (
    <button
      onClick={() => void baixar()}
      disabled={baixando}
      // O NOME DIZ O ARQUIVO. Sem isto o leitor de tela anuncia "botão", e
      // numa mensagem com três anexos a pessoa não tem como escolher qual
      // baixar — o nome está na tela, mas não no nome acessível.
      aria-label={`Baixar ${rotulo}`}
      className="flex items-center gap-3 rounded-lg border border-linha p-3 w-72 text-sm text-left hover:bg-fundo disabled:opacity-60"
    >
      <Icone nome="FileText" tamanho={28} className="text-perigo flex-shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block font-medium truncate">{rotulo}</span>
        <span className="block text-xs text-tinta-fraca">
          {tamanho(anexo.tamanho_bytes)} · verificado pelo antivírus
        </span>
      </span>
      <Icone nome="Download" tamanho={16} />
    </button>
  );
}

/**
 * Ação da barra da mensagem.
 *
 * O RÓTULO APARECE QUANDO HÁ ESPAÇO. A barra era só ícones — arquivar, spam,
 * excluir, pasta, marcador e não lida —, e seis desenhos em sequência não se
 * leem: a pessoa descobre o que cada um faz clicando, e "marcar como spam" e
 * "excluir" são caros de descobrir errando.
 *
 * O `aria-label` continua sempre, inclusive com o rótulo visível: ele é o
 * texto do `title` completo, com o atalho de teclado, que o rótulo curto não
 * mostra.
 */
function Botao({
  icone,
  titulo,
  rotulo,
  aoClicar,
}: {
  icone: Parameters<typeof Icone>[0]["nome"];
  titulo: string;
  rotulo?: string;
  aoClicar: () => void;
}) {
  return (
    <button
      onClick={aoClicar}
      className="flex items-center gap-1.5 rounded-full p-2 hover:bg-fundo"
      title={titulo}
      aria-label={titulo}
    >
      <Icone nome={icone} tamanho={18} />
      {rotulo && <span className="hidden text-sm lg:inline">{rotulo}</span>}
    </button>
  );
}

function Acao({ icone, rotulo, aoClicar }: { icone: Parameters<typeof Icone>[0]["nome"]; rotulo: string; aoClicar: () => void }) {
  return (
    <button
      onClick={aoClicar}
      className="flex items-center gap-2 rounded-full border border-linha px-4 py-2 text-sm hover:bg-fundo"
    >
      <Icone nome={icone} tamanho={16} /> {rotulo}
    </button>
  );
}

function Painel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex-1 bg-white rounded-tl-2xl grid place-items-center text-sm text-tinta-fraca">
      {children}
    </div>
  );
}
